import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useIntegrationConfigs } from '@/hooks/useIntegrationConfigs';
import { useParserBotSuppliers } from '@/hooks/useParserBotSuppliers';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { ParserStatisticsDashboard } from '@/components/ParserStatisticsDashboard';
import { RequiresAuthWarning } from '@/components/RequiresAuthWarning';
import { PageHeader } from '@/components/PageHeader';
import { RefreshCw, CheckCircle, AlertCircle, Shield, Zap, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export default function TumParserIntegration() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const workspaceContext = useWorkspace();
  const workspace = workspaceContext?.currentWorkspace;
  const { toast } = useToast();
  const { getIntegrationConfig, saveIntegrationConfig, testConnection, syncSuppliers: syncSuppliersMutation } = useIntegrationConfigs();
  const { suppliers, isLoading: suppliersLoading, toggleSupplier } = useParserBotSuppliers();

  const parserConfigQuery = getIntegrationConfig('parser_bot');
  const parserConfig = parserConfigQuery.data;
  const isLoading = parserConfigQuery.isLoading;

  const [apiBaseUrl, setApiBaseUrl] = useState('https://uat-api.billportal.io/api/v1');
  const [bearerToken, setBearerToken] = useState('');
  const [enableOpenAIFallback, setEnableOpenAIFallback] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaveAndSyncing, setIsSaveAndSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStep, setSyncStep] = useState('');

  // Initialize form with existing config
  useEffect(() => {
    if (parserConfig) {
      const creds = parserConfig.credentials as any || {};
      setApiBaseUrl(creds.api_base_url || parserConfig.api_base_url || 'https://uat-api.billportal.io/api/v1');
      setBearerToken(creds.bearer_token || '');
      setEnableOpenAIFallback(creds.enable_openai_fallback ?? parserConfig.enable_openai_fallback ?? true);
    }
  }, [parserConfig]);

  const validateDomain = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      const allowedDomains = ['billportal.ai', 'billportal.io'];
      return allowedDomains.some(domain => urlObj.hostname.endsWith(domain));
    } catch {
      return false;
    }
  };

  const normalizeApiBaseUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(p => p);
      let basePath = '';
      
      // Find if there's an /api path
      const apiIndex = pathParts.findIndex(p => p === 'api');
      if (apiIndex !== -1) {
        // Keep up to /api/v1 or just /api
        if (pathParts[apiIndex + 1] === 'v1') {
          basePath = '/' + pathParts.slice(0, apiIndex + 2).join('/');
        } else {
          basePath = '/' + pathParts.slice(0, apiIndex + 1).join('/');
        }
      } else {
        // No /api found, use root
        basePath = '/';
      }
      
      // Reconstruct clean base URL
      return `${urlObj.protocol}//${urlObj.host}${basePath}`.replace(/\/$/, '');
    } catch {
      return url;
    }
  };

  const handleSaveConfig = async () => {
    if (!workspace?.id) return;

    // Normalize URL first
    const normalizedUrl = normalizeApiBaseUrl(apiBaseUrl);
    if (normalizedUrl !== apiBaseUrl) {
      setApiBaseUrl(normalizedUrl);
      toast({
        title: 'URL normalized',
        description: `API URL has been normalized to: ${normalizedUrl}`,
      });
    }

    // Validate domain
    if (!validateDomain(normalizedUrl)) {
      toast({
        title: 'Invalid API URL',
        description: 'API URL must be from billportal.ai or billportal.io domains',
        variant: 'destructive',
      });
      return;
    }

    try {
      await saveIntegrationConfig.mutateAsync({
        integration_type: 'parser_bot',
        integration_name: 'BillPortal Parser',
        provider: 'billportal',
        credentials: {
          api_base_url: apiBaseUrl,
          bearer_token: bearerToken || null,
          enable_openai_fallback: enableOpenAIFallback,
          allowed_domains: ['billportal.ai', 'billportal.io'],
        },
        is_enabled: true,
      });

      toast({
        title: 'Configuration saved',
        description: 'Parser integration settings have been updated',
      });
    } catch (error: any) {
      toast({
        title: 'Failed to save configuration',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSyncSuppliers = async () => {
    setIsSyncing(true);
    try {
      await syncSuppliersMutation.mutateAsync(workspace?.id);
      toast({
        title: 'Suppliers synced',
        description: 'Supplier list has been updated from BillPortal API',
      });
    } catch (error: any) {
      toast({
        title: 'Sync failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    if (!workspace?.id) return;
    
    // Normalize URL first
    const normalizedUrl = normalizeApiBaseUrl(apiBaseUrl);
    if (normalizedUrl !== apiBaseUrl) {
      setApiBaseUrl(normalizedUrl);
      toast({
        title: 'URL normalized',
        description: `API URL has been normalized to: ${normalizedUrl}`,
      });
    }
    
    // Validate URL first
    if (!validateDomain(normalizedUrl)) {
      toast({
        title: 'Invalid API URL',
        description: 'Please enter a valid BillPortal API URL',
        variant: 'destructive',
      });
      return;
    }

    setIsTesting(true);
    try {
      await testConnection.mutateAsync({
        integrationType: 'parser_bot',
        apiBaseUrl: normalizedUrl,
        bearerToken: bearerToken,
      });
    } catch (error: any) {
      // Error already handled by mutation
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveAndSync = async () => {
    if (!workspace?.id) return;
    
    // Normalize URL first
    const normalizedUrl = normalizeApiBaseUrl(apiBaseUrl);
    if (normalizedUrl !== apiBaseUrl) {
      setApiBaseUrl(normalizedUrl);
    }
    
    // Validate domain first
    if (!validateDomain(normalizedUrl)) {
      toast({
        title: 'Invalid API URL',
        description: 'API URL must be from billportal.ai or billportal.io domains',
        variant: 'destructive',
      });
      return;
    }

    setIsSaveAndSyncing(true);
    setSyncProgress(0);
    
    try {
      // Step 1: Save configuration (33%)
      setSyncStep('Saving configuration...');
      setSyncProgress(10);
      
      await saveIntegrationConfig.mutateAsync({
        integration_type: 'parser_bot',
        integration_name: 'BillPortal Parser',
        provider: 'billportal',
        credentials: {
          api_base_url: normalizedUrl,
          bearer_token: bearerToken || null,
          enable_openai_fallback: enableOpenAIFallback,
          allowed_domains: ['billportal.ai', 'billportal.io'],
        },
        is_enabled: true,
      });
      
      setSyncProgress(33);
      toast({
        title: 'Configuration saved',
        description: 'Successfully saved Parser Bot settings',
      });
      
      // Step 2: Test connection (66%)
      setSyncStep('Testing connection...');
      setSyncProgress(40);
      
      await testConnection.mutateAsync({
        integrationType: 'parser_bot',
        apiBaseUrl: normalizedUrl,
        bearerToken: bearerToken,
      });
      
      setSyncProgress(66);
      toast({
        title: 'Connection successful',
        description: 'Connected to BillPortal API',
      });
      
      // Step 3: Sync suppliers (100%)
      setSyncStep('Syncing suppliers...');
      setSyncProgress(75);
      
      await syncSuppliersMutation.mutateAsync(workspace?.id);
      
      setSyncProgress(100);
      toast({
        title: 'Setup complete!',
        description: 'Parser Bot is ready to use. Redirecting...',
      });
      
      // Step 4: Redirect to Processing → Sources
      setTimeout(() => {
        navigate(`/w/${workspaceId}/dashboard/tum/bill-journey?stage=processing`);
      }, 1500);
      
    } catch (error: any) {
      toast({
        title: 'Setup failed',
        description: error.message,
        variant: 'destructive',
      });
      setSyncProgress(0);
      setSyncStep('');
    } finally {
      setTimeout(() => {
        setIsSaveAndSyncing(false);
        setSyncProgress(0);
        setSyncStep('');
      }, 2000);
    }
  };

  const getStatusBadge = () => {
    if (!parserConfig) {
      return <Badge variant="secondary">Not Configured</Badge>;
    }

    if (parserConfig.requires_auth) {
      return <Badge variant="destructive" className="flex items-center gap-1">
        <Shield className="h-3 w-3" />
        Requires Authentication
      </Badge>;
    }

    if (parserConfig.connection_status === 'connected') {
      return <Badge variant="default" className="bg-green-600">Active</Badge>;
    }

    if (parserConfig.connection_status === 'error') {
      return <Badge variant="destructive">Error</Badge>;
    }

    return <Badge variant="outline">Configured</Badge>;
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="BillPortal Parser Integration"
        description="Configure automated bill parsing for the TUM processing stage"
        breadcrumbs={[
          { label: 'TUM Dashboard', href: `/w/${workspaceId}/dashboard/tum` },
          { label: 'Bill Journey', href: `/w/${workspaceId}/dashboard/tum/bill-journey` },
          { label: 'Parser Integration' }
        ]}
      />

      {/* Status Banner */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Integration Status</CardTitle>
              <CardDescription>Current state of the parser integration</CardDescription>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
      </Card>

      {/* Authentication Warning */}
      {parserConfig?.requires_auth && (
        <RequiresAuthWarning
          lastError={parserConfig.last_error}
          lastErrorAt={parserConfig.last_error_at}
          authFailures={parserConfig.auth_failures}
          onConfigure={() => {}}
        />
      )}

      <Tabs defaultValue="configuration" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>API Configuration</CardTitle>
              <CardDescription>
                Configure the BillPortal API endpoint and authentication
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="apiBaseUrl">API Base URL *</Label>
                <Input
                  id="apiBaseUrl"
                  value={apiBaseUrl}
                  onChange={(e) => setApiBaseUrl(e.target.value)}
                  placeholder="https://uat-api.billportal.io/api/v1"
                />
                <p className="text-xs text-muted-foreground">
                  Must be from billportal.ai or billportal.io domains. Enter only the base URL (e.g., https://uat-api.billportal.io/api/v1), not the full endpoint path.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bearerToken">Bearer Token (Optional)</Label>
                <Input
                  id="bearerToken"
                  type="password"
                  value={bearerToken}
                  onChange={(e) => setBearerToken(e.target.value)}
                  placeholder="Enter bearer token if required"
                />
                <p className="text-xs text-muted-foreground">
                  The system will try without authentication first. Add a token only if the API requires it.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>OpenAI Fallback</Label>
                  <p className="text-xs text-muted-foreground">
                    Use OpenAI as backup when BillPortal API fails
                  </p>
                </div>
                <Switch
                  checked={enableOpenAIFallback}
                  onCheckedChange={setEnableOpenAIFallback}
                />
              </div>

              {isSaveAndSyncing && (
                <div className="space-y-2 p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{syncStep}</span>
                    <span className="text-muted-foreground">{syncProgress}%</span>
                  </div>
                  <Progress value={syncProgress} className="h-2" />
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveAndSync}
                  disabled={isSaveAndSyncing}
                  size="lg"
                >
                  {isSaveAndSyncing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting Up...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Save & Sync Suppliers
                    </>
                  )}
                </Button>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveConfig}
                    variant="outline"
                    disabled={isSaveAndSyncing}
                  >
                    Save Only
                  </Button>
                  <Button 
                    onClick={handleTestConnection} 
                    variant="outline"
                    disabled={isTesting || isSaveAndSyncing}
                  >
                    {isTesting ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <Zap className="mr-2 h-4 w-4" />
                        Test Connection
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Enabled Suppliers</CardTitle>
                  <CardDescription>
                    Manage which suppliers are enabled for automatic parsing
                  </CardDescription>
                </div>
                <Button 
                  onClick={handleSyncSuppliers} 
                  disabled={isSyncing}
                  variant="outline"
                >
                  {isSyncing ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Sync Suppliers
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {suppliersLoading ? (
                <div>Loading suppliers...</div>
              ) : (
                <div className="space-y-2">
                  {suppliers?.map((supplier) => (
                    <div 
                      key={supplier.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div>
                        <div className="font-medium">{supplier.supplier_name}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-4 mt-1">
                          <span>ID: {supplier.supplier_id}</span>
                          {supplier.total_parsed > 0 && (
                            <>
                              <span>Parsed: {supplier.total_parsed}</span>
                              <span className="text-green-600">
                                Success: {supplier.success_count} ({supplier.avg_confidence?.toFixed(0) || 0}% confidence)
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <Switch
                        checked={supplier.is_enabled}
                        onCheckedChange={(enabled) => toggleSupplier.mutate({ supplierId: supplier.id, enabled })}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistics" className="space-y-6">
          <ParserStatisticsDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
